import { LeaseLineIntersection, threeDeeScan } from 'utils/threeDeeScan'
import { pointInterp, pointInterpTVD } from 'utils/surveyFunctions'

import { Vector3 } from 'three'
import { appColors } from 'utils'
import { cloneDeep } from 'lodash'
import { getTargets3D } from './targets3d'

export function validateWellboreSurveyIndexValue(wellData, index) {
  if (!Array.isArray(wellData)) return 0
  if (wellData.length === 0) return 0
  if (!Array.isArray(wellData[0]?.data)) return 0
  if (wellData[0].data.length === 0) return 0
  if (index >= wellData[0].data.length) return 0
  if (index < 0) return wellData[0].data.length - 1
  return index
}

export function isArrayValid(wellData) {
  if (!Array.isArray(wellData)) return false
  if (wellData.length === 0) return false
  if (!Array.isArray(wellData[0]?.data)) return false
  if (wellData[0].data.length === 0) return false
  return true
}

function checkArrayDataValid(inputData) {
  if (!Array.isArray(inputData)) return false
  if (inputData.length === 0) return false

  return true
}

function getWellColor(wellType, principal = false) {
  if (wellType === 'Planned') {
    return !principal ? appColors.planTrajectoryColor : appColors.principalPlanColor
  }
  return appColors.surveyTrajectoryColor
}

function getAnnotations(outputArray, refWellData, annotations, scale) {
  annotations.forEach((annot) => {
    const { md, ns, ew, tvd } = pointInterp(refWellData, annot.md)

    if (md >= 0) {
      outputArray.push({
        x: ns * scale,
        y: -tvd * scale,
        z: ew * scale,
        name: annot.annotation,
      })
    }
  })
}

function getFormations(outputArray, refWellData, formations, scale) {
  for (let i = 0; i < formations.length - 1; i++) {
    const { md, ns, ew } = pointInterpTVD(refWellData, formations[i].tvd)

    outputArray.push({
      x: md >= 0 ? ns * scale : 0,
      y: -parseFloat(formations[i].tvd) * scale,
      z: md >= 0 ? ew * scale : 0,
      name: formations[i + 1].formation,
      found: md >= 0 ? true : false,
      md,
    })
  }
}

function getCasings(outputArray, refWellData, casing, scale) {
  casing.forEach((csg) => {
    const { md, ns, ew, tvd } = pointInterp(refWellData, csg.MD - 0.00001)

    if (md >= 0) {
      outputArray.push({
        x: ns * scale,
        y: -tvd * scale,
        z: ew * scale,
        name: `${csg.OD} ${csg.Type}`,
        od: csg.OD,
        md: md,
        HoleSize: csg.HoleSize,
      })
    }
  })
}

function createWellData(surveyData, name, color, scale, isPlan, isPrincipal = false, wellType) {
  if (!Array.isArray(surveyData)) return {}

  let wellData = {
    isPlan: isPlan,
    isPrincipalPlan: isPrincipal,
    name,
    color,
    wellType,
    data: [],
    svyData: [],
    zoneData: {
      display: false,
      fracHeightUp: 0,
      fracHeightDown: 0,
      fracWidth: 0,
      startMd: 0,
      endMd: 0,
      rotation: 0,
    },
    minSurveyDepth: 0,
    maxSurveyDepth: 0,
    maxRop: 0,
    avgRop: 0,
  }

  const CL_THRESH = 100

  let ropSum = 0
  let ropCount = 0

  surveyData.forEach((svy, i) => {
    if (i > 0) {
      const prevSvy = surveyData[i - 1]
      const cl = svy.md - prevSvy.md

      if (cl > CL_THRESH) {
        let curMd = prevSvy.md + CL_THRESH
        while (curMd < svy.md) {
          const interpPt = pointInterp(surveyData, curMd)
          if (interpPt?.md < 0) break
          wellData.data.push(new Vector3(interpPt.ns * scale, -interpPt.tvd * scale, interpPt.ew * scale))
          wellData.svyData.push({ ...interpPt })
          curMd += CL_THRESH
        }
      }
    }

    wellData.data.push(new Vector3(svy.ns * scale, -svy.tvd * scale, svy.ew * scale))
    wellData.svyData.push({ ...svy })
    if (i === 0) {
      wellData.minSurveyDepth = svy.md
    }

    if (i === surveyData.length - 1) {
      wellData.maxSurveyDepth = svy.md
    }

    if (svy.rop > wellData.maxRop) {
      wellData.maxRop = svy.rop
    }

    if (svy.rop > 0 && i > 0) {
      let cl = svy.md - surveyData[i - 1].md
      ropSum += svy.rop * cl
      ropCount += cl
    }
  })

  if (ropCount > 0) {
    wellData.avgRop = ropSum / ropCount
  }

  return wellData
}

export function createChartData(
  wellData,
  wellName,
  refWellSurveys,
  scale,
  projections,
  isPlan,
  targets = [],
  selectedRefWell,
) {
  let chartData = {
    refData: [],
    offsetData: [],
    formations: [],
    annotations: [],
    casing: [],
    targets: [],
    leaseLineInterSections: [],
    geosteering: [],
    minSurveyDepth: 0,
    maxSurveyDepth: 0,
    maxRop: 0,
    avgRop: 0,
  }

  let svyCopy = []
  if (checkArrayDataValid(wellData?.wellPlan?.surveyData) && checkArrayDataValid(wellData?.surveys)) {
    wellData.surveys.forEach((svy) => {
      svyCopy.push(threeDeeScan({ ...svy, tf: svy.tf }, wellData.wellPlan.surveyData))
    })
  } else {
    svyCopy = wellData?.surveys
  }

  if (Array.isArray(refWellSurveys) && refWellSurveys.length > 0) {
    svyCopy = []

    refWellSurveys.forEach((svy) => {
      svyCopy.push(
        threeDeeScan({ ...svy }, Array.isArray(wellData?.wellPlan?.surveyData) ? wellData.wellPlan.surveyData : []),
      )
    })
  }

  if (Array.isArray(projections) && projections.length > 1) {
    //Ignore the first survey as this will be a duplicate of the last survey ref well surveys
    for (let i = 1; i < projections.length; i++) {
      let newProj = threeDeeScan({ ...projections[i] }, wellData.wellPlan?.surveyData)
      newProj.isProjection = true
      svyCopy.push(newProj)
    }
  }

  let isRefWellPlan = isPlan
  if (selectedRefWell && selectedRefWell !== wellName) {
    chartData.offsetData.push(
      createWellData(
        svyCopy,
        wellName,
        getWellColor(isPlan ? 'Planned' : 'Actual'),
        scale,
        isPlan,
        false,
        isPlan ? 'Planned' : wellData?.wellType,
      ),
    )

    svyCopy = []

    if (Array.isArray(wellData?.offsetWells) && wellData.offsetWells.length > 0) {
      wellData.offsetWells.forEach((well) => {
        if (!Array.isArray(well.surveyData)) return

        if (selectedRefWell !== well.offsetWellbore) {
          return
        }

        isRefWellPlan = well.wellStatus === 'Planned' ? true : false

        svyCopy = cloneDeep(well.surveyData)
      })
    }
  }

  if (checkArrayDataValid(svyCopy)) {
    let isRefPrincipal = selectedRefWell === wellData?.wellPlan?.offsetWellbore

    chartData.refData.push(
      createWellData(
        svyCopy,
        selectedRefWell,
        getWellColor(isRefWellPlan ? 'Planned' : 'Actual', isRefPrincipal),
        scale,
        isRefWellPlan,
        false,
        isRefWellPlan ? 'Planned' : wellData?.wellType,
      ),
    )

    chartData.maxSurveyDepth = chartData.refData[0].maxSurveyDepth
    chartData.minSurveyDepth = chartData.refData[0].minSurveyDepth
    if (chartData.refData[0].maxRop > chartData.maxRop) {
      chartData.maxRop = chartData.refData[0].maxRop
    }

    chartData.avgRop = chartData.refData[0].avgRop
  }

  if (checkArrayDataValid(wellData?.wellPlan?.surveyData) && selectedRefWell !== wellData?.wellPlan?.offsetWellbore) {
    chartData.offsetData.push(
      createWellData(
        wellData.wellPlan.surveyData,
        wellData.wellPlan.offsetWellbore,
        getWellColor('Planned', true),
        scale,
        true,
        true,
        'Planned',
      ),
    )
  }

  let avgRop = chartData.avgRop
  let avgRopCount = chartData.avgRop > 0 ? 1 : 0

  if (checkArrayDataValid(wellData?.offsetWells)) {
    wellData.offsetWells.forEach((well) => {
      if (well.isPrincipal === true) {
        return
      }

      //Adds error ellipses to ref well
      if (well.offsetWellbore === wellName) {
        if (!Array.isArray(chartData.refData)) return
        if (chartData.refData.length === 0) return
        if (!Array.isArray(chartData.refData[0]?.svyData)) return
        if (!Array.isArray(well.surveyData)) return

        if (well?.zoneData) {
          chartData.refData[0].zoneData = { ...well.zoneData }
        }

        if (
          wellData.actualWellData.wellType &&
          wellData.actualWellData.wellType !== '' &&
          wellData.actualWellData.wellType !== 'Unknown'
        ) {
          chartData.refData[0].wellType = wellData.actualWellData.wellType
        }

        for (let i = 0; i < chartData.refData[0].svyData.length; i++) {
          let md = chartData.refData[0].svyData[i].md
          const interpSvy = pointInterp(well.surveyData, md)
          if (interpSvy?.md < 0) continue

          chartData.refData[0].svyData[i].semiMajor = interpSvy.semiMajor
          chartData.refData[0].svyData[i].semiMinor = interpSvy.semiMinor
          chartData.refData[0].svyData[i].covN = interpSvy.covN
          chartData.refData[0].svyData[i].covE = interpSvy.covE
          chartData.refData[0].svyData[i].covV = interpSvy.covV
          chartData.refData[0].svyData[i].covNe = interpSvy.covNe
          chartData.refData[0].svyData[i].covNv = interpSvy.covNv
          chartData.refData[0].svyData[i].covEv = interpSvy.covEv
        }

        return
      }

      if (well.offsetWellbore === selectedRefWell || well.offsetWellbore === wellName) {
        return
      }

      let offWellData = createWellData(
        well.surveyData,
        well.offsetWellbore,
        getWellColor(well.wellStatus),
        scale,
        well.wellStatus === 'Planned',
        false,
        well.wellStatus === 'Planned' ? 'Planned' : well.wellType,
      )

      if (well?.zoneData) {
        offWellData.zoneData = { ...well.zoneData }
      }

      if (offWellData.maxRop > chartData.maxRop) {
        chartData.maxRop = offWellData.maxRop
      }

      if (offWellData.avgRop > 0) {
        avgRop += offWellData.avgRop
        avgRopCount += 1
      }

      chartData.offsetData.push(offWellData)
    })
  }

  if (avgRopCount > 0) {
    chartData.avgRop = avgRop / avgRopCount
  }

  if (checkArrayDataValid(wellData?.surveys)) {
    if (checkArrayDataValid(wellData.actualWellData?.casing))
      getCasings(chartData.casing, wellData.surveys, wellData.actualWellData.casing, scale)
    if (checkArrayDataValid(wellData.actualWellData?.annotations))
      getAnnotations(chartData.annotations, wellData.surveys, wellData.actualWellData.annotations, scale)
    if (checkArrayDataValid(wellData.actualWellData?.lithologies))
      getFormations(chartData.formations, wellData.surveys, wellData.actualWellData.lithologies, scale)
  }

  if (Array.isArray(wellData?.surveys) && wellData.surveys.length > 0) {
    let maxSurveyTvd = 0

    for (let i = 0; i < wellData.surveys.length; i++) {
      const v = wellData.surveys[i]?.tvd
      if (Number.isFinite(v) && v > maxSurveyTvd) {
        maxSurveyTvd = v
      }
    }

    if (Array.isArray(targets) && targets.length > 0) {
      for (let i = 0; i < targets.length; i++) {
        if (targets[i]?.isLeaseLine && Array.isArray(targets[i]?.polygonPoints)) {
          for (let j = 0; j < targets[i].polygonPoints.length; j++) {
            const pt = targets[i].polygonPoints[j]
            if (pt?.top) pt.top.tvd = maxSurveyTvd
            if (pt?.bottom) pt.bottom.tvd = maxSurveyTvd
          }
        }
      }
    }
  }

  if (checkArrayDataValid(targets)) {
    chartData.targets = getTargets3D(targets, scale)
  }

  if (Array.isArray(chartData.targets) && Array.isArray(chartData.refData) && chartData.refData.length > 0) {
    chartData.targets.forEach((target) => {
      if (!target?.isLeaseLine || !Array.isArray(target?.points)) {
        return
      }

      let leaseLinePoints = []
      target.points.forEach((pt) => {
        leaseLinePoints.push({ ns: pt.top.x / scale, tvd: pt.top.y / scale, ew: pt.top.z / scale })
      })

      let interSections = LeaseLineIntersection(chartData.refData[0].svyData, leaseLinePoints)

      let interSectionLabels = []
      interSections.forEach((intersect) => {
        const { md, ns, ew, tvd } = pointInterp(wellData.surveys, intersect.md)

        if (md >= 0) {
          interSectionLabels.push({
            x: ns * scale,
            y: -tvd * scale,
            z: ew * scale,
            label: `${target?.text} Intersection\nMD: ${intersect.md.toFixed(2)}\nTVD: ${tvd.toFixed(2)}\nNS: ${ns.toFixed(2)}\nEW: ${ew.toFixed(2)}`,
          })
        }
      })

      chartData.leaseLineInterSections.push(...interSectionLabels)
    })
  }

  return chartData
}

export const toThreeColor = (color) => {
  if (typeof color !== 'string') return color

  const match = color.match(/^oklch\(\s*([\d.]+)%?\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+%?)?\s*\)$/i)

  if (!match) return color

  let L = parseFloat(match[1])
  const C = parseFloat(match[2])
  const h = (parseFloat(match[3]) * Math.PI) / 180

  // CSS percentages -> 0-1
  if (color.includes(`${match[1]}%`)) {
    L /= 100
  }

  // OKLCH -> OKLab
  const a = C * Math.cos(h)
  const b = C * Math.sin(h)

  // OKLab -> linear sRGB
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3

  let r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s
  let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  let blue = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s

  // Linear sRGB -> sRGB
  const gamma = (v) => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055)

  r = Math.round(Math.min(1, Math.max(0, gamma(r))) * 255)
  g = Math.round(Math.min(1, Math.max(0, gamma(g))) * 255)
  blue = Math.round(Math.min(1, Math.max(0, gamma(blue))) * 255)

  return `rgb(${r}, ${g}, ${blue})`
}

export const toHexColor = (color) => {
  if (typeof color !== 'string') return '#000000'

  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return color
  }

  const rgbColor = toThreeColor(color)

  const match = rgbColor.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i)

  if (!match) return '#000000'

  return `#${[match[1], match[2], match[3]].map((value) => Number(value).toString(16).padStart(2, '0')).join('')}`
}

export const toCanvasColor = (color, alpha = 1) => {
  if (typeof color !== 'string') return color

  // OKLCH
  const match = color.match(/^oklch\(\s*([\d.]+)%?\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+%?)?\s*\)$/i)

  if (match) {
    let L = parseFloat(match[1])
    const C = parseFloat(match[2])
    const h = (parseFloat(match[3]) * Math.PI) / 180

    if (color.includes(`${match[1]}%`)) {
      L /= 100
    }

    const a = C * Math.cos(h)
    const b = C * Math.sin(h)

    const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
    const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
    const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3

    let r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s
    let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
    let blue = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s

    const gamma = (v) => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055)

    r = Math.round(Math.min(1, Math.max(0, gamma(r))) * 255)
    g = Math.round(Math.min(1, Math.max(0, gamma(g))) * 255)
    blue = Math.round(Math.min(1, Math.max(0, gamma(blue))) * 255)

    return `rgba(${r}, ${g}, ${blue}, ${alpha})`
  }

  // #RGB
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    const r = parseInt(color[1] + color[1], 16)
    const g = parseInt(color[2] + color[2], 16)
    const b = parseInt(color[3] + color[3], 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // #RRGGBB
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const r = parseInt(color.slice(1, 3), 16)
    const g = parseInt(color.slice(3, 5), 16)
    const b = parseInt(color.slice(5, 7), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // rgb(...) / rgba(...)
  const rgbMatch = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i)

  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`
  }

  return color
}
